import { hexToOklch, oklchToHex } from "./oklch";

/**
 * Builds a full set of colour roles from a single brand colour, for a domain that has
 * no hand-tuned preset in palettes.json.
 *
 * The rule is the one the neutrals in the presets follow: take the brand hue, hold
 * chroma near zero, and step lightness to the four surface values. Never reuse another
 * brand's greys — a violet ground under an orange accent reads as a bug.
 *
 * The lightness and chroma steps below were measured off the five hand-tuned presets,
 * so seeding this with a preset's `pure` colour lands within a couple of percent of
 * that preset. theme.test.ts holds that line.
 */

export type DerivedRoles = {
  ground: string;
  panel: string;
  card: string;
  well: string;
  text: string;
  accent: string;
  ink: string;
  shadowRgb?: string;
};

type Step = { l: number; c: number };

const DARK: Record<"ground" | "panel" | "card" | "well" | "text" | "ink", Step> = {
  ground: { l: 0.194, c: 0.013 },
  panel: { l: 0.237, c: 0.021 },
  card: { l: 0.271, c: 0.024 },
  well: { l: 0.32, c: 0.028 },
  text: { l: 0.947, c: 0.011 },
  ink: { l: 0.229, c: 0.041 },
};

/**
 * The light steps are spread wider than the dark ones. Contrast is a ratio, so near
 * the white end a given lightness step buys much less separation than the same step
 * near black: the references' 99.0% panel against a 99.3% card measures 1.01:1, which
 * is no separation at all. These land each adjacent pair on the ~1.10:1 its dark
 * counterpart achieves.
 */
const LIGHT: Record<"ground" | "panel" | "card" | "well" | "text", Step> = {
  ground: { l: 0.93, c: 0.012 },
  panel: { l: 0.964, c: 0.005 },
  card: { l: 0.997, c: 0.004 },
  well: { l: 0.89, c: 0.021 },
  text: { l: 0.246, c: 0.03 },
};

/** Dark accents lift to roughly 74-84% lightness so 13px text on `panel` still holds. */
const DARK_ACCENT = { lift: 0.1, min: 0.735, max: 0.84 };
/** Light accents darken instead of lifting; the tint recipe keeps them pale on the page. */
const LIGHT_ACCENT_L = 0.47;
/** The light-mode shadow is a deep, slightly saturated cast of the brand hue. */
const LIGHT_SHADOW = { l: 0.28, c: 0.09 };

const clamp = (n: number, lo: number, hi: number) => (n < lo ? lo : n > hi ? hi : n);

export function deriveDark(seed: string): DerivedRoles {
  const { l, c, h } = hexToOklch(seed);
  const step = (s: Step) => oklchToHex({ l: s.l, c: s.c, h });
  return {
    ground: step(DARK.ground),
    panel: step(DARK.panel),
    card: step(DARK.card),
    well: step(DARK.well),
    text: step(DARK.text),
    ink: step(DARK.ink),
    accent: oklchToHex({ l: clamp(l + DARK_ACCENT.lift, DARK_ACCENT.min, DARK_ACCENT.max), c, h }),
  };
}

export function deriveLight(seed: string): DerivedRoles {
  const { c, h } = hexToOklch(seed);
  const step = (s: Step) => oklchToHex({ l: s.l, c: s.c, h });
  const panel = step(LIGHT.panel);
  const shadow = oklchToHex({ l: LIGHT_SHADOW.l, c: LIGHT_SHADOW.c, h });
  return {
    ground: step(LIGHT.ground),
    panel,
    card: step(LIGHT.card),
    well: step(LIGHT.well),
    text: step(LIGHT.text),
    accent: oklchToHex({ l: LIGHT_ACCENT_L, c, h }),
    // On a light page the accent shape is filled, so its text is the panel colour.
    ink: panel,
    shadowRgb: hexToRgbList(shadow),
  };
}

function hexToRgbList(hex: string): string {
  const h = hex.replace("#", "");
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)).join(", ");
}
